/**
 * <tws-countdown deadline="2026-12-31T20:00:00" timezone="Australia/Sydney">
 *
 * Fills whichever `[data-unit]` slots the markup provides, so the same element
 * drives the header's inline `DD:HH:MM:SS` readout and the hero's HRS/MIN/SEC
 * boxes. Hours wrap at 24 only when a days slot is present to carry the
 * overflow — without one, the hero shows a true total (`28` hours).
 *
 * Bare deadlines (no Z / ±offset) are read in `timezone` (theme setting).
 * An explicit offset on the deadline string always wins. DST is handled via
 * the IANA zone (e.g. Australia/Sydney).
 *
 * Add `deadline-from="<selector>"` to follow another countdown's deadline.
 *
 * A passed deadline removes the whole element rather than freezing at zero: a
 * stale date should leave a clean layout, not an expired timer.
 */

// Sections each include this file, so it can run more than once on a page;
// the class would be redeclared, so later runs stop here.
(() => {
  if (customElements.get('tws-countdown')) return;

  function twsZonedWallTimeToUtc(dateStr, timeZone) {
    const m = String(dateStr)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) return Date.parse(dateStr);

    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    const h = +(m[4] || 0);
    const mi = +(m[5] || 0);
    const s = +(m[6] || 0);
    const wanted = Date.UTC(y, mo - 1, d, h, mi, s);

    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    const partsOf = (ms) => {
      const out = {};
      for (const p of fmt.formatToParts(new Date(ms))) {
        if (p.type !== 'literal') out[p.type] = p.value;
      }
      return out;
    };

    let utc = wanted;
    for (let i = 0; i < 3; i++) {
      const p = partsOf(utc);
      const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
      const diff = wanted - asUtc;
      if (diff === 0) break;
      utc += diff;
    }
    return utc;
  }

  function twsParseDeadline(raw, timeZone) {
    if (!raw) return NaN;
    const trimmed = String(raw).trim();
    if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
      return Date.parse(trimmed);
    }
    if (!timeZone || timeZone === 'local') {
      return new Date(trimmed).getTime();
    }
    return twsZonedWallTimeToUtc(trimmed, timeZone);
  }

  class TwsCountdown extends HTMLElement {
    connectedCallback() {
      // `deadline-from` borrows another countdown's deadline (e.g. the header's),
      // so two timers on a page can't drift apart. Falls back to our own.
      // A comma list is a priority order: the first selector with a deadline wins.
      const from = this.getAttribute('deadline-from');
      const source =
        (from || '')
          .split(',')
          .map((sel) => sel.trim() && document.querySelector(`${sel.trim()}[deadline]`))
          .find(Boolean) || this;
      this.deadline = twsParseDeadline(
        source.getAttribute('deadline'),
        source.getAttribute('timezone')
      );

      // An impossible date (Feb 31, say) would otherwise render "NaN".
      if (Number.isNaN(this.deadline)) {
        this.remove();
        return;
      }

      this.units = {};
      for (const slot of this.querySelectorAll('[data-unit]')) {
        this.units[slot.dataset.unit] = slot;
      }

      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.floor((this.deadline - Date.now()) / 1000);

      if (remaining <= 0) {
        clearInterval(this.timer);
        this.remove();
        return;
      }

      const days = Math.floor(remaining / 86400);
      const hours = Math.floor(remaining / 3600);

      this.write('days', days);
      this.write('hours', this.units.days ? hours % 24 : hours);
      this.write('minutes', Math.floor(remaining / 60) % 60);
      this.write('seconds', remaining % 60);
    }

    write(unit, value) {
      const slot = this.units[unit];
      if (slot) slot.textContent = String(value).padStart(2, '0');
    }
  }

  customElements.define('tws-countdown', TwsCountdown);
})();
